import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Chip,
  Box,
  Typography,
  Avatar,
  MenuItem,
  Select,
  InputLabel,
  Alert,
  Switch,
  FormGroup
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Announcement as AnnouncementIcon,
  DateRange as DateIcon
} from '@mui/icons-material';
import { NoteType, NotePriority, User } from '../../utils/types';
import { createNote } from '../../services/noteService';
import { createNoteNotification } from '../../services/teamNotificationService';
import { getAllUsers } from '../../services/userService';

interface CreateNoteDialogProps {
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  currentUserName: string;
}

const CreateNoteDialog: React.FC<CreateNoteDialogProps> = ({
  open,
  onClose,
  currentUserId,
  currentUserName
}) => {
  const [noteType, setNoteType] = useState<NoteType>('personal');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState<NotePriority>('medium');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [sharedWith, setSharedWith] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [hasDueDate, setHasDueDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // تحميل قائمة المستخدمين
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      const users = await getAllUsers();
      // استبعاد المستخدم الحالي
      const filteredUsers = users.filter(user => user.id !== currentUserId && user.isActive);
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('فشل في تحميل قائمة المستخدمين');
    }
  };

  // إعادة تعيين النموذج
  const resetForm = () => {
    setNoteType('personal');
    setTitle('');
    setContent('');
    setPriority('medium');
    setTags([]);
    setTagInput('');
    setSharedWith([]);
    setDueDate(null);
    setHasDueDate(false);
    setError('');
  };

  // إغلاق النافذة
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // إضافة علامة
  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  // حذف علامة
  const handleDeleteTag = (tagToDelete: string) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };

  // التعامل مع الضغط على Enter في حقل العلامات
  const handleTagKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  // إنشاء الملاحظة
  const handleCreateNote = async () => {
    if (loading) return;

    // التحقق من صحة البيانات
    if (!title.trim() || !content.trim()) {
      setError('يجب إدخال العنوان والمحتوى');
      return;
    }

    if (noteType === 'shared' && sharedWith.length === 0) {
      setError('يجب اختيار مستخدم واحد على الأقل للمشاركة');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // إعداد بيانات الملاحظة
      const noteData: any = {
        title: title.trim(),
        content: content.trim(),
        authorId: currentUserId,
        authorName: currentUserName,
        type: noteType,
        priority,
        tags,
        sharedWith: noteType === 'shared' ? sharedWith.map(user => user.id) : [],
        isCompleted: false,
        isArchived: false
      };

      // إضافة تاريخ الاستحقاق فقط إذا كان موجوداً
      if (hasDueDate && dueDate) {
        noteData.dueDate = dueDate;
      }

      const noteId = await createNote(noteData);

      // إرسال إشعارات للمستخدمين المشاركين
      if (noteType === 'shared') {
        for (const user of sharedWith) {
          await createNoteNotification(
            user.id,
            currentUserName,
            title.trim(),
            noteId,
            'shared'
          );
        }
      }

      handleClose();
    } catch (error) {
      console.error('Error creating note:', error);
      setError('فشل في إنشاء الملاحظة');
    } finally {
      setLoading(false);
    }
  };

  // الحصول على عنوان النافذة
  const getDialogTitle = () => {
    switch (noteType) {
      case 'personal':
        return 'إنشاء ملاحظة شخصية';
      case 'shared':
        return 'إنشاء ملاحظة مشتركة';
      case 'announcement':
        return 'إنشاء إعلان';
      default:
        return 'إنشاء ملاحظة جديدة';
    }
  };

  // الحصول على وصف نوع الملاحظة
  const getNoteTypeDescription = () => {
    switch (noteType) {
      case 'personal':
        return 'ملاحظة خاصة بك فقط';
      case 'shared':
        return 'ملاحظة مشتركة مع أعضاء الفريق';
      case 'announcement':
        return 'إعلان عام لجميع أعضاء الفريق';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {getDialogTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getNoteTypeDescription()}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* نوع الملاحظة */}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                نوع الملاحظة
              </FormLabel>
              <RadioGroup
                value={noteType}
                onChange={(e) => setNoteType(e.target.value as NoteType)}
                row
              >
                <FormControlLabel
                  value="personal"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2">شخصية</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="shared"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon fontSize="small" />
                      <Typography variant="body2">مشتركة</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="announcement"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AnnouncementIcon fontSize="small" />
                      <Typography variant="body2">إعلان</Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* العنوان */}
            <TextField
              label="العنوان"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder="أدخل عنوان الملاحظة"
            />

            {/* المحتوى */}
            <TextField
              label="المحتوى"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="أدخل محتوى الملاحظة"
            />

            {/* الأولوية */}
            <FormControl fullWidth>
              <InputLabel>الأولوية</InputLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value as NotePriority)}
                label="الأولوية"
              >
                <MenuItem value="low">منخفضة</MenuItem>
                <MenuItem value="medium">متوسطة</MenuItem>
                <MenuItem value="high">عالية</MenuItem>
                <MenuItem value="urgent">عاجلة</MenuItem>
              </Select>
            </FormControl>

            {/* العلامات */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                العلامات
              </Typography>
              
              <TextField
                label="إضافة علامة"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                fullWidth
                size="small"
                placeholder="اكتب علامة واضغط Enter"
                InputProps={{
                  endAdornment: tagInput.trim() && (
                    <Button size="small" onClick={handleAddTag}>
                      إضافة
                    </Button>
                  )
                }}
              />

              {tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* تاريخ الاستحقاق */}
            <Box>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasDueDate}
                      onChange={(e) => setHasDueDate(e.target.checked)}
                    />
                  }
                  label="تحديد تاريخ استحقاق"
                />
              </FormGroup>

              {hasDueDate && (
                <TextField
                  label="تاريخ الاستحقاق"
                  type="datetime-local"
                  value={dueDate ? dueDate.toISOString().slice(0, 16) : ''}
                  onChange={(e) => setDueDate(e.target.value ? new Date(e.target.value) : null)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </Box>

            {/* المشاركة مع المستخدمين */}
            {noteType === 'shared' && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  مشاركة مع
                </Typography>
                
                <Autocomplete
                  multiple
                  options={availableUsers}
                  getOptionLabel={(option) => option.name}
                  value={sharedWith}
                  onChange={(_, newValue) => setSharedWith(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="ابحث عن مستخدمين للمشاركة"
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {option.name.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.role} - {option.branchName}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                        avatar={<Avatar sx={{ width: 24, height: 24 }}>{option.name.charAt(0)}</Avatar>}
                      />
                    ))
                  }
                />
              </Box>
            )}

            {/* رسالة خطأ */}
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>
            إلغاء
          </Button>
          <Button
            onClick={handleCreateNote}
            variant="contained"
            disabled={loading || !title.trim() || !content.trim()}
            sx={{ minWidth: 100 }}
          >
            {loading ? 'جاري الإنشاء...' : 'إنشاء'}
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default CreateNoteDialog;
